import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { billingSupportMailto } from '../api/apiErrorCodes'
import {
  fetchOrganizationBilling,
  type OrganizationBillingOverviewDto,
  type PlanPriceDto,
} from '../api/billingPayments'
import { formatApiErrorMessage } from '../api/apiErrorMessage'
import { trackMarketEvent } from '../analytics/marketEvents'
import { useAuth } from '../auth/AuthProvider'
import { PlanPricingCards } from '../components/billing/PlanPricingCards'
import { useOrganizationAccess } from '../hooks/useOrganizationAccess'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { StatTile, StatTileRow } from '../components/StatTile'
import { parseRegisterPlan, REGISTER_PLAN_LABELS } from '../legal/publicOffer'

const formatRub = (amount: number) =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(amount)

const formatTrialEnd = (iso: string | null | undefined) => {
  if (!iso) {
    return null
  }
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export const PaymentsScreen = ({ embedded = false }: { embedded?: boolean }) => {
  const { session } = useAuth()
  const [searchParams] = useSearchParams()
  const highlightedPlan = useMemo(() => parseRegisterPlan(searchParams.get('plan')), [searchParams])
  const { isOwner, requestOwnerAccess, ownerPaywallModal } = useOrganizationAccess()
  const organizationId = session?.organizationId ?? null
  const [overview, setOverview] = useState<OrganizationBillingOverviewDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadOverview = useCallback(async () => {
    if (organizationId == null || !isOwner) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await fetchOrganizationBilling(organizationId)
      setOverview(data)
    } catch (loadError) {
      setError(formatApiErrorMessage(loadError, 'Не удалось загрузить тарифы'))
    } finally {
      setLoading(false)
    }
  }, [isOwner, organizationId])

  useEffect(() => {
    void loadOverview()
  }, [loadOverview])

  useEffect(() => {
    if (searchParams.get('plan')) {
      trackMarketEvent('checkout_start', { plan: highlightedPlan, source: 'payments' })
    }
  }, [highlightedPlan, searchParams])

  const isTrial = overview?.current.subscription_status === 'trial'
  const trialEndsLabel = formatTrialEnd(overview?.current.period_end)

  const handleUpgrade = (planName: string) => {
    if (!requestOwnerAccess()) {
      return
    }
    trackMarketEvent('checkout_start', { plan: planName })
    window.location.href = billingSupportMailto(`Подключение тарифа ${planName}`)
  }

  const handleSelectPlan = (plan: PlanPriceDto) => {
    if (plan.plan === 'start') {
      return
    }
    handleUpgrade(plan.display_name)
  }

  if (!isOwner) {
    return (
      <div className={embedded ? undefined : 'market-page'}>
        {embedded ? null : <MarketPageHeader title="Платежи" description="Тарифы и подписка" />}
        <p className="market-billing-notice__text">
          Раздел доступен только владельцу организации. Обратитесь к владельцу для смены тарифа.
        </p>
        {ownerPaywallModal}
      </div>
    )
  }

  return (
    <div className={embedded ? 'market-payments--embedded' : 'market-page market-page--wide market-page--payments'}>
      {embedded ? null : (
        <MarketPageHeader
          title="Платежи"
          description="Тарифы Старт / Месяц / Год, пробный доступ и оформление подписки"
        />
      )}

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {overview ? (
        <>
          {isTrial ? (
            <section className="market-billing-notice" aria-label="Пробный период">
              <p>
                Сейчас действует <strong>пробный полный доступ по тарифу «Месяц»</strong>
                {trialEndsLabel ? <> до {trialEndsLabel}</> : null}. Он выдаётся при регистрации на
                «Старт» (один раз на аккаунт).
              </p>
              <p className="echat-muted">
                После окончания триала без оплаты кабинет вернётся на «Старт». Оформите «Месяц» или
                «Год», чтобы сохранить полный доступ.
              </p>
              <button
                type="button"
                className="market-auth__submit"
                onClick={() =>
                  handleUpgrade(
                    REGISTER_PLAN_LABELS[highlightedPlan === 'start' ? 'month' : highlightedPlan],
                  )
                }
              >
                Оформить подписку
              </button>
            </section>
          ) : null}

          {overview.current.subscription_status === 'pending' ? (
            <section className="market-billing-notice" aria-label="Ожидает оплаты">
              <p>
                Тариф выбран, доступ пока на «Старте». Запросите счёт, чтобы активировать «
                {REGISTER_PLAN_LABELS[highlightedPlan === 'start' ? 'month' : highlightedPlan]}».
              </p>
              <button
                type="button"
                className="market-auth__submit"
                onClick={() =>
                  handleUpgrade(
                    REGISTER_PLAN_LABELS[highlightedPlan === 'start' ? 'month' : highlightedPlan],
                  )
                }
              >
                Запросить счёт
              </button>
            </section>
          ) : null}

          <section className="market-pricing-status" aria-label="Текущая подписка">
            <div className="market-pricing-status__main">
              <p className="market-pricing-status__label">Сейчас подключено</p>
              <p className="market-pricing-status__plan">
                {overview.current.plan_display_name}
                <span className="market-pricing-status__status">
                  {overview.current.subscription_status_display_name}
                </span>
              </p>
            </div>
            <StatTileRow layout="inline" className="market-pricing-status__metrics">
              <StatTile
                variant="stat"
                label="Заведения"
                value={`${overview.current.venues_used} / ${overview.current.venues_limit}`}
              />
              {(overview.current.plan === 'month' || overview.current.plan === 'year') &&
              overview.current.paid_venue_slots > 0 ? (
                <StatTile variant="stat" label="Доп. слоты" value={overview.current.paid_venue_slots} />
              ) : null}
              {overview.current.period_end ? (
                <StatTile
                  variant="stat"
                  label={isTrial ? 'Триал до' : 'Оплачено до'}
                  value={new Date(overview.current.period_end).toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                />
              ) : null}
            </StatTileRow>
          </section>

          <PlanPricingCards
            catalog={overview.catalog}
            currentPlan={overview.current.plan}
            onSelectPlan={handleSelectPlan}
            formatRub={formatRub}
            addonVenueMonthlyRub={overview.addon_venue_monthly_rub}
            yearBaseVenues={overview.premium_base_venues}
          />
        </>
      ) : null}

      {ownerPaywallModal}
    </div>
  )
}
